import { useEffect, useMemo, useRef, useState } from "react";
import { circles, CANVAS_W, CANVAS_H } from "./circles";

import {
  buildAnimConfig,
  clamp01,
  easeInOut,
  type CircleAnim,
} from "./animationConfig";

type OffscreenPos = {
  // final resting position in % of viewport
  finalLeftPct: number;
  finalTopPct: number;
  // offscreen starting position expressed the same way (can be < 0 or > 100)
  startLeftPct: number;
  startTopPct: number;
  sizePx: number;
  color: string;
};

// Assembly zone: circles finish assembling by this scroll progress.
// After this point, the Hero stays fully assembled ("holds") for the remaining
// scroll span so the user sees the completed hero for a long time before it
// finally slides out to reveal the next section.
const ASSEMBLY_END = 0.65;

// Nav fade window (finishes right before assembly end)
const NAV_START = 0.5;
const NAV_END = 0.6;

// Title fade window (finishes right at assembly end, replacing the % counter)
const TITLE_START = 0.58;
const TITLE_END = 0.68;

export default function Hero() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [progress, setProgress] = useState(0);
  const [viewport, setViewport] = useState({
    w: typeof window !== "undefined" ? window.innerWidth : 1600,
    h: typeof window !== "undefined" ? window.innerHeight : 900,
  });

  const animCfg = useMemo<CircleAnim[]>(() => buildAnimConfig(), []);

  useEffect(() => {
    const onResize = () => {
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    let rafId = 0;
    let pending = false;

    const compute = () => {
      pending = false;
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // Progress within Hero's own scroll span (0 at top, 1 when Hero fully scrolled through)
      const total = el.offsetHeight - window.innerHeight;
      const scrolled = -rect.top;
      const raw = total > 0 ? scrolled / total : 0;
      setProgress(clamp01(raw));
    };

    const onScroll = () => {
      if (pending) return;
      pending = true;
      rafId = requestAnimationFrame(compute);
    };

    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(rafId);
    };
  }, []);

  // Continuous time (seconds) for the swimming tail wave.
  const [swimTime, setSwimTime] = useState(0);
  useEffect(() => {
    let rafId = 0;
    const start = performance.now();
    const tick = (now: number) => {
      setSwimTime((now - start) / 1000);
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);

  // Shrink the whole fish a bit so there's breathing room around it.
  const FISH_SHRINK = 0.78;
  const scale =
    Math.max(viewport.w / CANVAS_W, viewport.h / CANVAS_H) * FISH_SHRINK;

  const scaledCircles: OffscreenPos[] = circles.map((c, i) => {
    const cfg = animCfg[i];
    const scaledCanvasW = CANVAS_W * scale;
    const scaledCanvasH = CANVAS_H * scale;
    const offsetX = (viewport.w - scaledCanvasW) / 2;
    const offsetY = (viewport.h - scaledCanvasH) / 2;

    const finalCenterX = offsetX + c.x * scale;
    const finalCenterY = offsetY + c.y * scale;
    const finalLeftPct = (finalCenterX / viewport.w) * 100;
    const finalTopPct = (finalCenterY / viewport.h) * 100;

    let startLeftPct: number;
    let startTopPct: number;
    switch (cfg.edge) {
      case "left":
        startLeftPct = cfg.outsidePercent;
        startTopPct = cfg.crossPercent;
        break;
      case "right":
        startLeftPct = cfg.outsidePercent;
        startTopPct = cfg.crossPercent;
        break;
      case "top":
        startLeftPct = cfg.crossPercent;
        startTopPct = cfg.outsidePercent;
        break;
      case "bottom":
      default:
        startLeftPct = cfg.crossPercent;
        startTopPct = cfg.outsidePercent;
        break;
    }

    return {
      finalLeftPct,
      finalTopPct,
      startLeftPct,
      startTopPct,
      sizePx: c.size * scale,
      color: c.color,
    };
  });

  // Remap so the assembly animation runs across [0, ASSEMBLY_END] and then
  // stays fully complete (animProgress = 1) for the rest of the scroll span.
  const animProgress =
    progress >= ASSEMBLY_END ? 1 : clamp01(progress / ASSEMBLY_END);
  const animPercentInt = Math.round(animProgress * 100);

  const navP = clamp01((progress - NAV_START) / (NAV_END - NAV_START));
  const titleP = clamp01((progress - TITLE_START) / (TITLE_END - TITLE_START));

  return (
    <div
      ref={containerRef}
      className="relative w-full"
      style={{ height: "300vh", backgroundColor: "#000000" }}
    >
      {/* Fixed hero viewport — permanently pinned to the screen while the user scrolls
          through the Hero's tall spacer. It never moves; the next section slides up
          on top of it (via a higher z-index) to cover it. */}
      <div
        className="fixed top-0 left-0 w-full overflow-hidden"
        style={{
          height: "100vh",
          backgroundColor: "#000000",
          fontFamily: "'Space Grotesk', system-ui, sans-serif",
          zIndex: 0,
          pointerEvents: "none",
        }}
      >
        {/* Top nav */}
        <nav
          className="absolute left-0 right-0 top-0 z-20 flex items-center justify-between"
          style={{
            padding: "28px 40px",
            opacity: navP,
            transform: `translateY(${(1 - navP) * -14}px)`,
            willChange: "opacity, transform",
            pointerEvents: navP > 0.5 ? "auto" : "none",
          }}
        >
          <div
            className="text-white"
            style={{ fontWeight: 700, fontSize: 18, letterSpacing: -0.2 }}
          >
            Strawberry Group
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="whitespace-nowrap cursor-pointer"
              style={{
                background: "#E9E9E9",
                color: "#000",
                borderRadius: 9999,
                padding: "10px 22px",
                fontWeight: 600,
                fontSize: 14,
                border: "none",
              }}
            >
              Contact Us
            </button>
            <button
              type="button"
              className="whitespace-nowrap cursor-pointer flex items-center gap-2"
              style={{
                background: "transparent",
                color: "#fff",
                border: "1px solid #fff",
                borderRadius: 9999,
                padding: "9px 20px",
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              <span>Menu</span>
              <span
                aria-hidden
                className="inline-flex flex-col justify-center"
                style={{ width: 16, height: 14, gap: 3 }}
              >
                <span
                  style={{
                    display: "block",
                    height: 1.5,
                    width: "100%",
                    background: "#fff",
                  }}
                />
                <span
                  style={{
                    display: "block",
                    height: 1.5,
                    width: "100%",
                    background: "#fff",
                  }}
                />
              </span>
            </button>
          </div>
        </nav>

        {/* Circles */}
        {scaledCircles.map((sc, i) => {
          const cfg = animCfg[i];
          const localRaw = (animProgress - cfg.startAt) / cfg.duration;
          const local = clamp01(localRaw);
          const eased = easeInOut(local);

          let leftPct =
            sc.startLeftPct + (sc.finalLeftPct - sc.startLeftPct) * eased;
          let topPct =
            sc.startTopPct + (sc.finalTopPct - sc.startTopPct) * eased;

          const swayAmp = circles[i].swayAmp;
          if (swayAmp && eased > 0.98) {
            const freq = 1.6;
            const wavelength = 900;
            const phase =
              swimTime * freq * Math.PI * 2 -
              (circles[i].x / wavelength) * Math.PI * 2;
            const dyCanvas = Math.sin(phase) * swayAmp;
            const dyPct = ((dyCanvas * scale) / viewport.h) * 100;
            topPct += dyPct;
          }

          const opacity = eased;
          const scaleVal = 0.3 + 0.7 * eased;

          return (
            <div
              key={circles[i].id}
              style={{
                position: "absolute",
                left: `${leftPct}%`,
                top: `${topPct}%`,
                width: sc.sizePx,
                height: sc.sizePx,
                borderRadius: "50%",
                background: sc.color,
                transform: `translate(-50%, -50%) scale(${scaleVal})`,
                opacity,
                willChange: "transform, opacity, left, top",
                pointerEvents: "none",
              }}
            />
          );
        })}

        {/* Scroll progress indicator (bottom-left) */}
        <div
          className="absolute z-10"
          style={{
            left: "3vw",
            bottom: "6vh",
            color: "#ECECEC",
            fontWeight: 800,
            fontSize: "clamp(48px, 8vw, 130px)",
            lineHeight: 1,
            letterSpacing: -2,
            fontFamily: "'Space Grotesk', system-ui, sans-serif",
            opacity: 1 - titleP,
            transition: "none",
          }}
        >
          {animPercentInt}%
        </div>

        {/* Title (bottom-left, revealed near end of scroll) */}
        <div
          className="absolute z-10"
          style={{
            left: "3vw",
            bottom: "6vh",
            opacity: titleP,
            transform: `translateY(${(1 - titleP) * 24}px)`,
            willChange: "opacity, transform",
            fontFamily: "'Space Grotesk', system-ui, sans-serif",
            fontWeight: 800,
            fontSize: "clamp(30px, 6.2vw, 96px)",
            letterSpacing: -1.5,
            lineHeight: 1.02,
          }}
        >
          <div style={{ color: "#8A8A8A" }}>Great investments start</div>
          <div style={{ color: "#ECECEC" }}>
            <b style={{ fontWeight: 800 }}>with radical vision.</b>
          </div>
        </div>
      </div>
    </div>
  );
}